import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { ISSUE_SEVERITY } from "contracts/enums/core";
import { CORE_WEB_VITALS_CONFIDENCE_LEVEL } from "contracts/enums/web-vitals";
import type { MapPreviewView } from "../../enums/map";
import { MAP_PREVIEW_VIEW } from "../../enums/map";
import { JOURNEY_FRICTION_KIND } from "../../enums/scan";
import { buildJourneyPreview } from "../scan/journeyModel";
import type { PriorityFlowConcern } from "../scan/journeyTypes";
import { buildPriorityFlowPreview } from "../scan/priorityFlows";
import type { MapCurrentGroup, MapPlan } from "./mapTypes";

export type MapBaselineGroup = {
  key: string;
  label: string;
  pageCount: number;
  assessedCount: number;
  averageScore: number | null;
  concernCount: number;
  highPriorityConcernCount: number;
  noOnwardPageCount: number;
};

export type MapPreviewSource = {
  website: string;
  runId: string | null;
  status: NonNullable<OverviewResponse["lastRun"]>["status"] | null;
  finishedAt: Date | null;
};

export type MapPreviewModel = {
  flowPreview: ReturnType<typeof buildPriorityFlowPreview>;
  concerns: PriorityFlowConcern[];
  groups: MapBaselineGroup[];
  hotspots: MapBaselineGroup[];
  highPriorityConcernCount: number;
  source: MapPreviewSource;
};

export function buildMapPreview(plan: MapPlan): MapPreviewModel {
  const journeyPreview = buildJourneyPreview(plan.data);
  const flowPreview = buildPriorityFlowPreview(plan.data, journeyPreview.paths);
  const concerns = uniqueConcerns(
    flowPreview.all.flatMap((flow) => flow.concerns),
  );
  const groups = plan.currentGroups.map((group) =>
    baselineGroup(group, concerns),
  );
  return {
    flowPreview,
    concerns,
    groups,
    hotspots: [...groups]
      .filter((group) => group.concernCount > 0 || group.noOnwardPageCount > 0)
      .sort(compareHotspots)
      .slice(0, 6),
    highPriorityConcernCount: concerns.filter(isHighPriorityConcern).length,
    source: mapPreviewSource(plan.data),
  };
}

function mapPreviewSource(data: OverviewResponse): MapPreviewSource {
  const run = data.lastRun;
  return {
    website:
      data.site.displayName ??
      data.site.title ??
      data.site.clientName ??
      data.site.domain,
    runId: run?.id ?? null,
    status: run?.status ?? null,
    finishedAt: run?.finishedAt ?? null,
  };
}

export function mapPreviewView(
  value: string | string[] | undefined,
): MapPreviewView {
  const selected = Array.isArray(value) ? value[0] : value;
  if (selected === "architecture" || selected === "migration") return selected;
  return MAP_PREVIEW_VIEW.BASELINE;
}

function baselineGroup(
  group: MapCurrentGroup,
  concerns: PriorityFlowConcern[],
): MapBaselineGroup {
  const pageIds = new Set(group.facts.map((fact) => fact.page.id));
  const related = concerns.filter((concern) =>
    concern.affectedPages.some((page) => pageIds.has(page.id)),
  );
  const scores = group.facts.flatMap((fact) =>
    typeof fact.page.contentFitness?.score === "number"
      ? [fact.page.contentFitness.score]
      : [],
  );
  return {
    key: group.key,
    label: group.label,
    pageCount: group.facts.length,
    assessedCount: scores.length,
    averageScore:
      scores.length > 0
        ? Math.round(
            scores.reduce((sum, score) => sum + score, 0) / scores.length,
          )
        : null,
    concernCount: related.length,
    highPriorityConcernCount: related.filter(isHighPriorityConcern).length,
    noOnwardPageCount: group.facts.filter((fact) => fact.outboundCount === 0)
      .length,
  };
}

function uniqueConcerns(concerns: PriorityFlowConcern[]) {
  return [
    ...new Map(concerns.map((concern) => [concern.key, concern])).values(),
  ];
}

function isHighPriorityConcern(concern: PriorityFlowConcern): boolean {
  return (
    concern.friction.kind === JOURNEY_FRICTION_KIND.BROKEN_LINK ||
    concern.friction.kind === JOURNEY_FRICTION_KIND.NO_ONWARD_LINK ||
    concern.friction.issue?.severity === ISSUE_SEVERITY.CRITICAL ||
    concern.friction.issue?.severity === CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH
  );
}

function compareHotspots(
  left: MapBaselineGroup,
  right: MapBaselineGroup,
): number {
  return (
    right.highPriorityConcernCount - left.highPriorityConcernCount ||
    right.concernCount - left.concernCount ||
    right.noOnwardPageCount - left.noOnwardPageCount ||
    (left.averageScore ?? 101) - (right.averageScore ?? 101) ||
    left.label.localeCompare(right.label)
  );
}
